// src/components/Header.js
import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "./AuthContext";
import ThemeToggle from "./ThemeToggle";
import { ThemeContext } from "../context/ThemeContext";
import { LanguageContext } from "../context/LanguageContext";
import useGuardedNavigate from "../hooks/useGuardedNavigate";
import {
  clearSeoLearningReturnPath,
  consumeSeoLearningReturnPath,
  peekSeoLearningReturnPath,
} from "../demo/seoLearningResume";

const CONTAINER_MAX_WIDTH = 1500;
const PILL_H = 40;

function useViewportWidth() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  useEffect(() => {
    const onResize = () => setW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return w;
}

const iconStyle = { width: 18, height: 18, display: "inline-block", verticalAlign: "middle" };

function HomeMonoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={iconStyle}>
      <path d="M3 10.8L12 3l9 7.8v9.2h-6.2v-5.6H9.2V20H3z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

function StudentMonoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={iconStyle}>
      <path d="M3 8l9-4 9 4-9 4z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M7 10v4.2c0 1.8 2.4 3.3 5 3.3s5-1.5 5-3.3V10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M21 8v5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function ParentMonoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={iconStyle}>
      <circle cx="8" cy="8" r="2.7" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="16" cy="8" r="2.7" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M3.8 18.5c.4-2.4 2.2-4 4.2-4s3.8 1.6 4.2 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M11.8 18.5c.4-2.4 2.2-4 4.2-4s3.8 1.6 4.2 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function TutorMonoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={iconStyle}>
      <circle cx="7.5" cy="7.5" r="2.5" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M3.5 16.5c.5-2.2 2.1-3.8 4-3.8s3.5 1.6 4 3.8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <rect x="13.5" y="5.5" width="7" height="5.8" rx="1.2" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M12.5 14h9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M15 18h4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function UserMonoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={iconStyle}>
      <circle cx="12" cy="8" r="3.2" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M5 19c.7-3 3.2-5 7-5s6.3 2 7 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function AdminMonoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={iconStyle}>
      <path d="M12 3l7 3v5c0 4.5-2.9 8.1-7 9.9-4.1-1.8-7-5.4-7-9.9V6z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M12 8v8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M8.8 11.8h6.4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function InvestmentMonoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={iconStyle}>
      <path d="M4 19V10M10 19V5M16 19v-7M3 19h18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 8l6-5 6 6 4-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LogoutMonoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={iconStyle}>
      <path d="M10 4H5v16h5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14 8l5 4-5 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 12h10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function LockClosedMonoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={iconStyle}>
      <rect x="5" y="10" width="14" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M8 10V8a4 4 0 118 0v2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function LockOpenMonoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={iconStyle}>
      <rect x="5" y="10" width="14" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M10 10V8a4 4 0 017-2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function LoginMonoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={iconStyle}>
      <circle cx="8" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M11 12h10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M18 12l-2.5-2.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M18 12l-2.5 2.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function RegisterMonoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={iconStyle}>
      <circle cx="9" cy="8" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M3.8 18c.5-2.4 2.4-4 5.2-4s4.7 1.6 5.2 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M18 8v6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M15 11h6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function SettingsMonoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={iconStyle}>
      <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
      <path
        d="M19.4 15a1.7 1.7 0 00.3 1.9l.1.1-2 2-.1-.1a1.7 1.7 0 00-1.9-.3 1.7 1.7 0 00-1 1.5V20h-3v-.1a1.7 1.7 0 00-1-1.5 1.7 1.7 0 00-1.9.3l-.1.1-2-2 .1-.1a1.7 1.7 0 00.3-1.9 1.7 1.7 0 00-1.5-1H4v-3h.1a1.7 1.7 0 001.5-1 1.7 1.7 0 00-.3-1.9l-.1-.1 2-2 .1.1a1.7 1.7 0 001.9.3 1.7 1.7 0 001-1.5V4h3v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.9-.3l.1-.1 2 2-.1.1a1.7 1.7 0 00-.3 1.9 1.7 1.7 0 001.5 1h.1v3h-.1a1.7 1.7 0 00-1.5 1z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FeedbackMonoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={iconStyle}>
      <path d="M5 5h14v10H8l-3 3z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M12 8v4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M12 15h.01" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

const Header = () => {
  const { user: authenticatedUser, logout, isAuthReady } = useContext(AuthContext);
  const { theme } = useContext(ThemeContext);
  const { t } = useContext(LanguageContext);
  const location = useLocation();
  const navigate = useGuardedNavigate();
  const isPublicDemoPreview = authenticatedUser?.role === "admin"
    && location.pathname.startsWith("/demo/matura")
    && new URLSearchParams(location.search).get("publicPreview") === "1";
  const user = isPublicDemoPreview ? null : authenticatedUser;

  const headerRef = useRef(null);
  const [isHeaderFullyHidden, setIsHeaderFullyHidden] = useState(false);
  const [suppressMiniHeaderByFullscreen, setSuppressMiniHeaderByFullscreen] = useState(false);
  const [seoReturnPath, setSeoReturnPath] = useState("");
  const hideTRef = useRef(null);

  const [miniCollapsed, setMiniCollapsed] = useState(() => {
    try {
      return localStorage.getItem("miniHeaderCollapsed") === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("miniHeaderCollapsed", miniCollapsed ? "1" : "0");
    } catch {}
  }, [miniCollapsed]);

  const vw = useViewportWidth();
  const NARROW = vw <= 640;
  const COMPACT = vw <= 900;
  const WIDE = vw >= 1180;
  const TINY = vw <= 360;

  const textColor = theme === "light" ? "#000" : "#fff";
  const bgColor = theme === "light" ? "#ffffff" : "#000000";
  const borderColor = theme === "light" ? "#000" : "#fff";

  const isStudent = user?.role === "student";
  const isParent = user?.role === "parent";
  const isTutor = user?.role === "tutor";
  const isAdmin = user?.role === "admin";
  const hasInvestmentAccess = isAdmin || user?.investmentAccess === true;

  useEffect(() => {
    if (!user) {
      setSeoReturnPath("");
      return;
    }
    const target = peekSeoLearningReturnPath();
    const current = `${location.pathname}${location.search}${location.hash}`;
    if (target && current === target) {
      clearSeoLearningReturnPath();
      setSeoReturnPath("");
      return;
    }
    setSeoReturnPath(target);
  }, [user, location.pathname, location.search, location.hash]);

  useEffect(() => {
    void location;
  }, [location]);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        const hidden = entry.intersectionRatio === 0;
        if (hideTRef.current) window.clearTimeout(hideTRef.current);
        hideTRef.current = window.setTimeout(() => {
          setIsHeaderFullyHidden(hidden);
        }, 80);
      },
      { root: null, threshold: [0, 0.01, 1] }
    );

    obs.observe(el);
    return () => {
      if (hideTRef.current) window.clearTimeout(hideTRef.current);
      obs.disconnect();
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const root = document.body;
    if (!root) return undefined;

    const compute = () => {
      const hasWhiteboardFullscreen =
        root.classList.contains("inline-whiteboard-fullscreen-active") ||
        root.classList.contains("whiteboard-fullscreen-active");
      setSuppressMiniHeaderByFullscreen(hasWhiteboardFullscreen);
    };

    compute();
    const obs = new MutationObserver(() => compute());
    obs.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  const scrollToPageTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const goTo = (path) => navigate(path);
  const openGlobalSettings = () => window.dispatchEvent(new Event("matematron:open-settings"));
  const openGlobalFeedback = () => window.dispatchEvent(new Event("matematron:open-feedback"));

  const topGridStyle = NARROW
    ? { display: "flex", flexDirection: "column", gap: 10 }
    : {
        display: "grid",
        gridTemplateColumns: WIDE ? "minmax(360px, 1fr) auto" : "minmax(260px, 1fr) auto",
        gap: 12,
        alignItems: "center",
      };

  const rightRowStyle = {
    display: "flex",
    gap: NARROW ? 8 : 10,
    alignItems: "center",
    justifyContent: NARROW ? "center" : "flex-end",
    flexWrap: "wrap",
  };

  const pillStyle = pill(textColor, borderColor, NARROW);
  const iconPillStyle = iconPill(textColor, borderColor, NARROW);

  const panelOuterStyle = {
    width: "100%",
    maxWidth: "none",
    margin: 0,
    padding: 0,
    boxSizing: "border-box",
    border: `2px solid ${borderColor}`,
    borderLeft: 0,
    borderRight: 0,
    borderRadius: 0,
    boxShadow: "none",
    overflow: "hidden",
    transform: "translateZ(0)",
  };

  const supportsBackdrop =
    typeof CSS !== "undefined" &&
    (CSS.supports("backdrop-filter: blur(1px)") || CSS.supports("-webkit-backdrop-filter: blur(1px)"));

  const panelInnerStyle = {
    padding: NARROW ? "10px 12px" : WIDE ? "12px clamp(28px, 4vw, 56px)" : "12px 20px",
    boxSizing: "border-box",
    background: bgColor,
    color: textColor,
    fontFamily: "var(--font-main)",
    overflowX: "hidden",
    minHeight: !isAuthReady ? (NARROW ? 148 : 132) : undefined,
    ...(supportsBackdrop
      ? { backdropFilter: "blur(12px) saturate(120%)", WebkitBackdropFilter: "blur(12px) saturate(120%)" }
      : {}),
  };

  const showMiniBar = isHeaderFullyHidden && !miniCollapsed && !suppressMiniHeaderByFullscreen;
  const showHandle = isHeaderFullyHidden && miniCollapsed && !suppressMiniHeaderByFullscreen;

  const brandLinkStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: NARROW ? "center" : "flex-start",
    gap: NARROW ? 9 : 10,
    minWidth: 0,
    textDecoration: "none",
    color: textColor,
  };

  const brandBannerStyle = {
    display: "block",
    width: NARROW ? "min(250px, calc(100vw - 92px))" : WIDE ? "300px" : "240px",
    maxWidth: "100%",
    height: "auto",
    minWidth: 0,
  };

  const userLinksRowStyle = {
    display: "flex",
    gap: NARROW ? 8 : 10,
    justifyContent: NARROW ? "center" : "flex-end",
    flexWrap: "wrap",
    paddingTop: COMPACT ? 0 : 2,
    paddingBottom: 0,
  };

  const statusStyle = {
    whiteSpace: NARROW ? "normal" : "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: NARROW ? "0.9rem" : "0.95rem",
    textAlign: NARROW ? "center" : "right",
    minWidth: 0,
  };

  const brandNode = (
    <Link to="/" style={brandLinkStyle} aria-label="Matematron">
      <img
        src={theme === "light" ? "/banner-light.png" : "/banner-dark.png"}
        alt="Matematron"
        width={1279}
        height={235}
        loading="eager"
        fetchPriority="high"
        decoding="async"
        style={brandBannerStyle}
      />
    </Link>
  );

  return (
    <>
      <header
        ref={headerRef}
        data-matematron-main-header
        style={{ width: "100%", marginBottom: 0 }}
      >
        <div className="glass-container" style={panelOuterStyle}>
          <div style={panelInnerStyle}>
            <div style={{ maxWidth: CONTAINER_MAX_WIDTH, margin: "0 auto", display: "flex", flexDirection: "column", gap: 10 }}>
              {!isAuthReady ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }} aria-hidden="true">
                  <div style={topGridStyle}>
                    {brandNode}
                    <div style={{ ...rightRowStyle, minHeight: 44 }}>
                      <div className="skeleton-block" style={{ width: 44, height: 40, borderRadius: 12 }} />
                      <div className="skeleton-block" style={{ width: 44, height: 40, borderRadius: 12 }} />
                      <div className="skeleton-block" style={{ width: 124, height: 40, borderRadius: 12 }} />
                      <div className="skeleton-block" style={{ width: 142, height: 40, borderRadius: 12 }} />
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: NARROW ? 8 : 10,
                      justifyContent: "center",
                      flexWrap: "wrap",
                      paddingTop: 4,
                      paddingBottom: 4,
                      minHeight: 44,
                    }}
                  >
                    <div className="skeleton-block" style={{ width: 150, height: 40, borderRadius: 12 }} />
                    <div className="skeleton-block" style={{ width: 152, height: 40, borderRadius: 12 }} />
                    <div className="skeleton-block" style={{ width: 170, height: 40, borderRadius: 12 }} />
                  </div>
                </div>
              ) : (
                <>
                  <div style={topGridStyle}>
                    {brandNode}

                    <div style={{ display: "flex", alignItems: "center", justifyContent: NARROW ? "center" : "flex-end", gap: 12, flexWrap: "wrap", minWidth: 0 }}>
                      <div style={statusStyle}>
                        {user ? (
                          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: NARROW ? "center" : "flex-end", gap: 6, flexWrap: "wrap" }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <LockClosedMonoIcon />
                              <span>{t("header.loggedAs")}</span>
                            </span>
                            <strong>{user.displayName || user.username || user.email}</strong>
                            {isStudent && <span>(<StudentMonoIcon /> {t("header.roleStudent")})</span>}
                            {isParent && <span>(<ParentMonoIcon /> {t("header.roleParent")})</span>}
                            {isTutor && <span>(<TutorMonoIcon /> {t("header.roleTutor")})</span>}
                            {isAdmin && <span>(<AdminMonoIcon /> {t("header.roleAdmin")})</span>}
                          </span>
                        ) : (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <LockOpenMonoIcon />
                            <span>{t("header.guestMode")}</span>
                          </span>
                        )}
                      </div>

                      {user ? (
                        <Link to="/konto" style={pillStyle} title={t("header.userPanel")}>
                          <UserMonoIcon />
                          {!TINY && <span style={{ marginLeft: 6 }}>Profil</span>}
                        </Link>
                      ) : null}

                      <div style={rightRowStyle}>
                        <Link to="/" title={t("header.home")} aria-label={t("header.home")} style={iconPillStyle}>
                          <HomeMonoIcon />
                        </Link>

                        {isAdmin && (
                          <div style={iconPillStyle}>
                            <ThemeToggle compact />
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={openGlobalSettings}
                          title={t("feedback.settingsTitle")}
                          aria-label={t("feedback.settingsTitle")}
                          style={iconPillStyle}
                        >
                          <SettingsMonoIcon />
                        </button>

                        <button
                          type="button"
                          onClick={openGlobalFeedback}
                          title={t("feedback.buttonTitle")}
                          aria-label={t("feedback.buttonTitle")}
                          style={iconPillStyle}
                        >
                          <FeedbackMonoIcon />
                        </button>

                        {user ? (
                          <button
                            onClick={async () => {
                              await logout();
                              navigate("/login");
                            }}
                            style={{ ...pillStyle, fontSize: 0 }}
                            title={t("header.logout")}
                          >
                            <span style={{ display: "inline-flex", alignItems: "center", fontSize: "1rem" }}>
                              <LogoutMonoIcon />
                              {!TINY && <span style={{ marginLeft: 6 }}>{t("header.logout")}</span>}
                            </span>
                          </button>
                        ) : (
                          <>
                            <Link to="/login" style={{ ...pillStyle, fontSize: 0 }} title={t("header.login")}>
                              <span style={{ display: "inline-flex", alignItems: "center", fontSize: "1rem" }}>
                                <LoginMonoIcon /> {!TINY && <span style={{ marginLeft: 6 }}>{t("header.login")}</span>}
                              </span>
                            </Link>
                            <Link to="/register" style={{ ...pillStyle, fontSize: 0 }} title={t("header.register")}>
                              <span style={{ display: "inline-flex", alignItems: "center", fontSize: "1rem" }}>
                                <RegisterMonoIcon /> {!TINY && <span style={{ marginLeft: 6 }}>{t("header.register")}</span>}
                              </span>
                            </Link>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {user && (
                    <div style={userLinksRowStyle}>
                      {isAdmin && (
                        <Link to="/uczen" style={pillStyle}>
                          <StudentMonoIcon /> {!TINY && <span style={{ marginLeft: 6 }}>{t("header.studentPanel")}</span>}
                        </Link>
                      )}

                      {(isParent || isAdmin) && (
                        <Link to="/rodzic" style={pillStyle}>
                          <ParentMonoIcon /> {!TINY && <span style={{ marginLeft: 6 }}>{t("header.parentPanel")}</span>}
                        </Link>
                      )}

                      {(isTutor || isAdmin) && (
                        <Link to="/korepetytor" style={pillStyle}>
                          <TutorMonoIcon /> {!TINY && <span style={{ marginLeft: 6 }}>{t("header.tutorPanel")}</span>}
                        </Link>
                      )}

                      {hasInvestmentAccess && (
                        <Link to="/panel-inwestora" style={pillStyle}>
                          <InvestmentMonoIcon /> {!TINY && <span style={{ marginLeft: 6 }}>{t("header.investorPanel")}</span>}
                        </Link>
                      )}

                      {isAdmin && (
                        <Link to="/admin" style={pillStyle}>
                          <AdminMonoIcon /> {!TINY && <span style={{ marginLeft: 6 }}>{t("header.adminPanel")}</span>}
                        </Link>
                      )}
                    </div>
                  )}
                </>
              )}

              {user && seoReturnPath ? (
                <div
                  role="status"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                    flexWrap: "wrap",
                    padding: "8px 10px",
                    borderTop: "1px solid rgba(255,153,0,0.45)",
                    fontSize: 14,
                  }}
                >
                  <span>Twoje ostatnie zadania i notatki czekają w rozpoczętym zagadnieniu.</span>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => {
                      const target = consumeSeoLearningReturnPath();
                      setSeoReturnPath("");
                      if (target) goTo(target);
                    }}
                    style={{ minHeight: 34, padding: "6px 12px" }}
                  >
                    Kontynuuj naukę
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    aria-label="Ukryj przycisk kontynuacji"
                    onClick={() => {
                      clearSeoLearningReturnPath();
                      setSeoReturnPath("");
                    }}
                    style={{ minWidth: 34, minHeight: 34, padding: "5px 9px" }}
                  >
                    ×
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      <MiniStickyHeader
        show={showMiniBar}
        user={user}
        theme={theme}
        isStudent={isStudent}
        isParent={isParent}
        isTutor={isTutor}
        isAdmin={isAdmin}
        hasInvestmentAccess={hasInvestmentAccess}
        onScrollTop={scrollToPageTop}
        onGoStudent={() => goTo("/uczen")}
        onGoParent={() => goTo("/rodzic")}
        onGoTutor={() => goTo("/korepetytor")}
        onGoInvestor={() => goTo("/panel-inwestora")}
        onGoAccount={() => goTo("/konto")}
        onOpenSettings={openGlobalSettings}
        onOpenFeedback={openGlobalFeedback}
        onCollapse={() => setMiniCollapsed(true)}
      />

      <MiniHeaderHandle show={showHandle} theme={theme} onExpand={() => setMiniCollapsed(false)} />
    </>
  );
};

function MiniStickyHeader({
  show,
  user,
  theme,
  isStudent,
  isParent,
  isTutor,
  isAdmin,
  hasInvestmentAccess,
  onScrollTop,
  onGoStudent,
  onGoParent,
  onGoTutor,
  onGoInvestor,
  onGoAccount,
  onOpenSettings,
  onOpenFeedback,
  onCollapse,
}) {
  const { t } = useContext(LanguageContext);
  const textColor = theme === "light" ? "#000" : "#fff";
  const bgColor = theme === "light" ? "rgba(255,255,255,0.92)" : "rgba(0,0,0,0.86)";
  const borderColor = theme === "light" ? "rgba(0,0,0,0.25)" : "rgba(255,255,255,0.18)";
  const miniLogoSrc = theme === "light" ? "/matematron-face.png" : "/matematron-face-light.png";

  const name = user?.displayName || user?.username || user?.email || "";
  const roleLabel = isStudent
    ? t("header.roleStudent")
    : isParent
    ? t("header.roleParent")
    : isTutor
    ? t("header.roleTutor")
    : isAdmin
    ? t("header.roleAdmin")
    : "";

  const canShowPanels = Boolean(user);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9998,
        padding: "8px 12px",
        pointerEvents: show ? "auto" : "none",
        opacity: show ? 1 : 0,
        transform: show ? "translateY(0px)" : "translateY(-12px)",
        transition: "opacity 180ms ease, transform 180ms ease",
      }}
    >
      <div style={{ maxWidth: CONTAINER_MAX_WIDTH, width: "100%", margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            borderRadius: 12,
            border: `1px solid ${borderColor}`,
            background: bgColor,
            color: textColor,
            backdropFilter: "blur(10px) saturate(130%)",
            WebkitBackdropFilter: "blur(10px) saturate(130%)",
            padding: "8px 10px",
          }}
        >
          <div
            style={{
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              gap: 8,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: 13,
              opacity: 0.95,
            }}
            title={name}
          >
            <img
              src={miniLogoSrc}
              alt=""
              width={24}
              height={24}
              loading="eager"
              decoding="async"
              style={{
                width: 24,
                height: 24,
                borderRadius: 7,
                border: "1px solid rgba(255,153,0,0.55)",
                objectFit: "contain",
                flexShrink: 0,
              }}
            />
            {user ? (
              <>
                <strong style={{ fontWeight: 700 }}>{name}</strong>
                {roleLabel ? <span style={{ opacity: 0.75 }}> • {roleLabel}</span> : null}
              </>
            ) : (
              <span style={{ opacity: 0.8 }}>{t("header.guestModeShort")}</span>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
            <Link
              to="/"
              title={t("header.home")}
              aria-label={t("header.home")}
              style={miniIconButtonStyle(textColor, borderColor)}
            >
              <HomeMonoIcon />
            </Link>

            {canShowPanels && isAdmin && (
              <button
                type="button"
                onClick={onGoStudent}
                title={t("header.studentPanel")}
                aria-label={t("header.studentPanel")}
                style={miniIconButtonStyle(textColor, borderColor)}
              >
                <StudentMonoIcon />
              </button>
            )}

            {canShowPanels && (isParent || isAdmin) && (
              <button
                type="button"
                onClick={onGoParent}
                title={t("header.parentPanel")}
                aria-label={t("header.parentPanel")}
                style={miniIconButtonStyle(textColor, borderColor)}
              >
                <ParentMonoIcon />
              </button>
            )}

            {canShowPanels && (isTutor || isAdmin) && (
              <button
                type="button"
                onClick={onGoTutor}
                title={t("header.tutorPanel")}
                aria-label={t("header.tutorPanel")}
                style={miniIconButtonStyle(textColor, borderColor)}
              >
                <TutorMonoIcon />
              </button>
            )}

            {canShowPanels && hasInvestmentAccess && (
              <button
                type="button"
                onClick={onGoInvestor}
                title={t("header.investorPanel")}
                aria-label={t("header.investorPanel")}
                style={miniIconButtonStyle(textColor, borderColor)}
              >
                <InvestmentMonoIcon />
              </button>
            )}

            {canShowPanels && (
              <button
                type="button"
                onClick={onGoAccount}
                title={t("header.userPanel")}
                aria-label={t("header.userPanel")}
                style={miniIconButtonStyle(textColor, borderColor)}
              >
                <UserMonoIcon />
              </button>
            )}

            {isAdmin && (
              <div style={{ ...miniIconButtonStyle(textColor, borderColor), padding: 0 }}>
                <ThemeToggle compact />
              </div>
            )}

            <button
              type="button"
              onClick={onOpenSettings}
              title={t("feedback.settingsTitle")}
              aria-label={t("feedback.settingsTitle")}
              style={miniIconButtonStyle(textColor, borderColor)}
            >
              <SettingsMonoIcon />
            </button>

            <button
              type="button"
              onClick={onOpenFeedback}
              title={t("feedback.buttonTitle")}
              aria-label={t("feedback.buttonTitle")}
              style={miniIconButtonStyle(textColor, borderColor)}
            >
              <FeedbackMonoIcon />
            </button>

            <button
              type="button"
              onClick={onScrollTop}
              title={t("header.scrollTop")}
              aria-label={t("header.scrollTop")}
              style={{ ...miniIconButtonStyle(textColor, borderColor), fontSize: 16 }}
            >
              ↑
            </button>

            <button
              type="button"
              onClick={onCollapse}
              title={t("header.hideBar")}
              aria-label={t("header.hideBar")}
              style={miniIconButtonStyle(textColor, borderColor)}
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniHeaderHandle({ show, theme, onExpand }) {
  const { t } = useContext(LanguageContext);
  const textColor = theme === "light" ? "#000" : "#fff";
  const bgColor = theme === "light" ? "rgba(255,255,255,0.92)" : "rgba(0,0,0,0.86)";
  const borderColor = theme === "light" ? "rgba(0,0,0,0.25)" : "rgba(255,255,255,0.18)";

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 9998,
        pointerEvents: show ? "auto" : "none",
        opacity: show ? 1 : 0,
        transformOrigin: "top center",
        transition: "opacity 180ms ease, transform 180ms ease",
      }}
    >
      <button
        type="button"
        onClick={onExpand}
        title={t("header.showBar")}
        aria-label={t("header.showBar")}
        style={{
          border: `1px solid ${borderColor}`,
          background: bgColor,
          color: textColor,
          backdropFilter: "blur(10px) saturate(130%)",
          WebkitBackdropFilter: "blur(10px) saturate(130%)",
          width: 56,
          height: 28,
          borderBottomLeftRadius: 999,
          borderBottomRightRadius: 999,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
          lineHeight: 1,
          fontSize: 16,
        }}
      >
        ▽
      </button>
    </div>
  );
}

function pill(color, borderColor, compact = false) {
  const h = compact ? 36 : PILL_H;
  const isNarrow = compact;

  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: h,
    padding: isNarrow ? "0 10px" : "0 14px",
    borderRadius: 10,
    textDecoration: "none",
    color,
    border: `1px solid ${borderColor}`,
    background: "transparent",
    fontFamily: "var(--font-main)",
    fontSize: isNarrow ? "0.95rem" : "1rem",
    cursor: "pointer",
    whiteSpace: isNarrow ? "normal" : "nowrap",
    textAlign: "center",
    maxWidth: "100%",
    boxSizing: "border-box",
    flexShrink: 1,
    minWidth: 0,
  };
}

function iconPill(color, borderColor, compact = false) {
  const h = compact ? 36 : PILL_H;
  const base = pill(color, borderColor, compact);
  return { ...base, width: h, padding: 0, flexShrink: 0, whiteSpace: "nowrap" };
}

function miniIconButtonStyle(color, borderColor) {
  return {
    width: 34,
    height: 34,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 10,
    border: `1px solid ${borderColor}`,
    background: "transparent",
    color,
    cursor: "pointer",
    padding: 0,
    flexShrink: 0,
    lineHeight: 1,
  };
}

export default Header;
